// Gestion du contexte d'authentification
import React, { createContext, useState, useEffect } from 'react';
import { getApiBase } from '../config/env';

// Création du contexte
export const AuthContext = createContext({
    user: null,
    loading: true,
    login: () => {},
    logout: () => {},
});

// Fournisseur d'authentification
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fonction pour récupérer le token
    const getToken = () => localStorage.getItem('token');

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const refreshToken = async (tokenToRefresh) => {
        const BASE = getApiBase();
        try {
            const response = await fetch(`${BASE}/auth/refresh-token`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${tokenToRefresh}` },
            });
            if (response.ok) {
                const data = await response.json();
                localStorage.setItem('token', data.token);
                return data.token;
            }
            return null;
        } catch (error) {
            console.error('Erreur de rafraîchissement du token :', error);
            return null;
        }
    };

    // AJOUT D'UNE FONCTION ASYNCHRONE DE VALIDATION (EVITER LE FLASH LOGIN)
    useEffect(() => {
        const validateToken = async () => {
            const MAX_RETRIES = 3;
            let retries = 0;
            let currentToken = getToken();

            if (!currentToken) {
                setLoading(false);
                return;
            }

            while (retries <= MAX_RETRIES) {
                try {
                    const BASE = getApiBase();
                    const response = await fetch(`${BASE}/auth/validate-token`, {
                        headers: { Authorization: `Bearer ${currentToken}` },
                    });

                    if (response.ok) {
                        const data = await response.json();
                        setUser({ email: data.email });
                        setLoading(false);
                        return;
                    }

                    if (response.status === 401) {
                        const newToken = await refreshToken(currentToken);
                        if (newToken) {
                            currentToken = newToken;
                            continue;
                        } else {
                            logout();
                            setLoading(false);
                            return;
                        }
                    }

                    // Erreurs transitoires ou autres erreurs HTTP
                    retries++;
                    if (retries > MAX_RETRIES) {
                        logout();
                        setLoading(false);
                        return;
                    }
                    const delay = Math.pow(2, retries - 1) * 1000;
                    await sleep(delay);
                } catch (error) {
                    console.error('Erreur de validation du token :', error);
                    retries++;
                    if (retries > MAX_RETRIES) {
                        logout();
                        setLoading(false);
                        return;
                    }
                    const delay = Math.pow(2, retries - 1) * 1000;
                    await sleep(delay);
                }
            }

            logout();
            setLoading(false);
        };

        validateToken();
    }, []);

    const login = (userData) => {
        // GARANTIT QUE LE TOKEN EST STOCKÉ AVANT DE CHANGER L'ÉTAT
        if (userData && userData.token) {
            localStorage.setItem('token', userData.token);
            setUser(userData);
        }
    };
    
    const logout = () => {
        setUser(null);
        localStorage.removeItem('token');
    };

    // AFFICHE UN CHARGEMENT TANT QUE LA VALIDATION N'EST PAS FAITE
    if (loading) {
        return <div>Chargement de la session...</div>;
    }

    return (
        <AuthContext.Provider value={{ user, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};
